/**
 * Guide Overview — the entry point. Frames what the Proactive Communications
 * Agent does (consistent with the welcome dialog's copy), carries the
 * fictive-data disclaimer as a left-accent callout, and lays out what's live
 * today against what's on the horizon. Claims nothing beyond the agreed
 * roadmap.
 */

import { CircleDot, Sparkles } from "lucide-react";

import { type GuideBulletItem, GuideBulletList, GuidePage } from "@/features/guide/guide-page";

const LIVE: GuideBulletItem[] = [
  { text: "Order-lifecycle event emails, sent automatically as orders progress" },
  { text: "Backorder handling as its own exception lane, outside the normal six-step flow" },
  { text: "A per-order sent-email log, reviewable at any time" },
  {
    text: "Preferred-account share tiers, with rep-approved nudges drafted for accounts trending below target",
  },
  { text: "Full audit logging across every send, suppression, and approval" },
];

const HORIZON: GuideBulletItem[] = [
  { text: "Richer customer notification preferences, beyond opt in / opt out" },
  { text: "Configurable tier thresholds per account segment" },
  { text: "Periodic synthesized account-health briefs" },
  { text: "Moving from fictive data to governed, real data" },
];

export function GuideOverviewPage() {
  return (
    <GuidePage
      eyebrow="Application guide"
      title="A walkthrough of the Proactive Communications Agent."
      intro="The agent runs two human-in-the-loop workflows on fictive data: automated order-event comms across the order lifecycle (including backorders), and rep-approved nudges that keep preferred accounts on track toward their order-share benefit tiers. This guide explains both surfaces: what they do, what is wired up today, and what is on the horizon. Pick a topic from the rail, or start with either workflow."
      cta={{ to: "/guide/ordering", label: "See how ordering comms work" }}
    >
      {/* Fictive-data disclaimer — the same sentence used everywhere else in
          the app, rendered as a left-accent callout. */}
      <section className="border-muted bg-accent-light rounded-lg border-l-4 p-4">
        <p className="text-foreground text-sm font-medium">
          Fictive data for the sake of demonstrating functionality.
        </p>
        <p className="text-muted mt-1 text-xs leading-5">
          Every account, order, and email in this demo is synthetic, built to show what the agent
          can do with current technology. Nothing here reflects a real Johnson &amp; Johnson
          customer or conversation.
        </p>
      </section>

      <section className="border-border bg-card rounded-lg border shadow-sm">
        <div className="divide-border grid grid-cols-1 divide-y lg:grid-cols-2 lg:divide-x lg:divide-y-0">
          <div className="p-6">
            <div className="flex items-center gap-2">
              <Sparkles className="text-muted h-4 w-4" aria-hidden />
              <h2 className="text-foreground text-sm font-semibold">What&rsquo;s live today</h2>
            </div>
            <p className="text-muted mt-1 text-xs">Wired up end-to-end and ready to demo.</p>
            <GuideBulletList items={LIVE} className="mt-4" />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2">
              <CircleDot className="text-faint h-4 w-4" aria-hidden />
              <h2 className="text-foreground text-sm font-semibold">On the horizon</h2>
            </div>
            <p className="text-muted mt-1 text-xs">Planned for the next iterations.</p>
            <GuideBulletList
              items={HORIZON.map((b) => ({ ...b, tone: "muted" as const }))}
              className="mt-4"
            />
          </div>
        </div>
      </section>
    </GuidePage>
  );
}
